import { ref } from "vue";
import ApiService from "@/core/services/ApiService";
import { debounce } from "lodash";
import * as yup from "yup";

const CHECK_USERNAME_LOADING = ref(false);
export default CHECK_USERNAME_LOADING;

export function initYupValidate() {
  yup.setLocale({
    mixed: {
      default: "${label}验证无效",
      required: "${label}为必填字段",
      oneOf: "${label}必须是以下值之一：${values}",
      notOneOf: "${label}不能是以下值之一：${values}",
      defined: "必须定义${label}",
    },
    string: {
      length: "${path}必须正好为${length}个字符”，",
      min: "${path}至少输入${min}个字符",
      max: "${path}最多输入${max}个字符",
      matches: '${path}必须匹配以下项："${regex}"',
      email: "${path}必须是有效的电子邮件地址",
      url: "${path}必须是有效的URL",
      uuid: "${path}必须是有效的UUID",
      trim: "${path}必须是修剪字符串",
      lowercase: "${path}必须是小写字符串”，",
      uppercase: "${path}必须是大写字符串”",
    },
    number: {
      min: "${path}必须大于或等于${min}",
      max: "${path}必须小于或等于${max}",
      lessThan: "${path}必须小于${less}",
      moreThan: "${path}必须大于${more}",
      positive: "${path}必须是正整数",
      negative: "${path}必须是负整数",
      integer: "${path}必须是整数",
    },
    date: {
      min: "${path}字段必须晚于${min}",
      max: "${path}字段必须早于${max}",
    },
    boolean: {
      isValue: "${path}字段必须为${value}",
    },
    object: {
      noUnknown: "${path}字段有未指定的键：${unknown}",
    },
    array: {
      min: "${path}字段必须至少有${min}项",
      max: "${path}字段必须小于或等于${max}项",
      length: "${path}必须有${length}项",
    },
  });

  const yupSequence = <T>(
    name: string,
    ...schemas: ReadonlyArray<yup.Schema>
  ): yup.TestConfig<T> => {
    return {
      name,
      test: async (value, context) => {
        try {
          for (const schema of schemas) {
            await schema.validate(value, { context });
          }
        } catch (error) {
          if (error instanceof yup.ValidationError) {
            // for some reason the original error's "path" is empty
            return context.createError({ message: error.message });
          }
          throw error;
        }
        return true;
      },
    };
  };

  //用户名
  yup.addMethod(
    yup.string,
    "username",
    function (
      errorMessage = "请输入合法的用户名，字母、数字或者英文符号[_]组合或手机号或电子邮箱"
    ) {
      const re =
        /^([a-zA-Z][a-zA-Z0-9_]{4,36}|(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}|((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))$/i;
      return this.matches(re, {
        name: "username",
        message: errorMessage,
        excludeEmptyString: true,
      });
    }
  );

  // //手机号码
  yup.addMethod(
    yup.string,
    "mobile",
    function (errorMessage = "请输入正确的手机号码") {
      const re =
        /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/i;
      return this.matches(re, {
        name: "mobile",
        message: errorMessage,
        excludeEmptyString: true,
      });
    }
  );

  // //电话号码 座机号码
  yup.addMethod(
    yup.string,
    "phone",
    function (errorMessage = "请输入正确的电话号码") {
      const re = /^(0\d{2,3})-?(\d{7,8})$/i;
      return this.matches(re, {
        name: "phone",
        message: errorMessage,
        excludeEmptyString: true,
      });
    }
  );

  // //电话或手机号码
  yup.addMethod(
    yup.string,
    "tel",
    function (errorMessage = "请输入正确的电话号码") {
      const re =
        /^(((0\d{2,3})-?(\d{7,8}))|((13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}))$/i;
      return this.matches(re, {
        name: "phone",
        message: errorMessage,
        excludeEmptyString: true,
      });
    }
  );

  // //姓名：2-4个中文字符正则
  yup.addMethod(
    yup.string,
    "chinesename",
    function (errorMessage = "请输入正确的中文姓名") {
      const re = /^[\u4e00-\u9fa5]{2,4}$/i;
      return this.matches(re, {
        name: "chinesename",
        message: errorMessage,
        excludeEmptyString: true,
      });
    }
  );

  // //身份证
  yup.addMethod(
    yup.string,
    "chinesename",
    function (errorMessage = "请输入正确的身份证号码") {
      const re = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/i;
      return this.matches(re, {
        name: "chinesename",
        message: errorMessage,
        excludeEmptyString: true,
      });
    }
  );

  // //邮政编码
  yup.addMethod(
    yup.string,
    "chinesename",
    function (errorMessage = "请输入正确的邮政编码") {
      const re = /^[1-9]\d{5}$/i;
      return this.matches(re, {
        name: "chinesename",
        message: errorMessage,
        excludeEmptyString: true,
      });
    }
  );

  // yup.addMethod(yup.string, "minWords", function (min, errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Please enter at least {0} words.";
  //     return value.match(/\b\w+\b/g).length >= min
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "maxWords", function (max, errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Please enter {0} words or less.";
  //     return value.match(/\b\w+\b/g).length <= max
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "rangeWords", function (min, max, errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Please enter between {0} and {1} words.";
  //     return value.match(/\b\w+\b/g).length >= min &&
  //       value.match(/\b\w+\b/g).length <= max
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "extension", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Please enter a value with a valid extension.";
  //     return value.match(/\\.(png|jpe?g|gif)$/gi)
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "ipv4", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Please enter a valid IP v4 address.";
  //     return value.match(
  //       /^(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)$/i
  //     )
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });

  // yup.addMethod(yup.string, "ipv6", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Please enter a valid IP v6 address.";
  //     return value.match(
  //       /^((([0-9A-Fa-f]{1,4}:){7}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}:[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){5}:([0-9A-Fa-f]{1,4}:)?[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){4}:([0-9A-Fa-f]{1,4}:){0,2}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){3}:([0-9A-Fa-f]{1,4}:){0,3}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){2}:([0-9A-Fa-f]{1,4}:){0,4}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){6}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(([0-9A-Fa-f]{1,4}:){0,5}:((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|(::([0-9A-Fa-f]{1,4}:){0,5}((\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b)\.){3}(\b((25[0-5])|(1\d{2})|(2[0-4]\d)|(\d{1,2}))\b))|([0-9A-Fa-f]{1,4}::([0-9A-Fa-f]{1,4}:){0,5}[0-9A-Fa-f]{1,4})|(::([0-9A-Fa-f]{1,4}:){0,6}[0-9A-Fa-f]{1,4})|(([0-9A-Fa-f]{1,4}:){1,7}:))$/i
  //     )
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });

  // yup.addMethod(yup.string, "lettersonly", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Letters only please";
  //     return value.match(/^[a-z]+$/i)
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "letterswithbasicpunc", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Letters or punctuation only please";
  //     return value.match(/^[a-z\-.,()'"\s]+$/i)
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "mobileNL", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     errorMessage = "Please specify a valid mobile number";
  //     return value.match(
  //       /^((\+|00(\s|\s?\-\s?)?)31(\s|\s?\-\s?)?(\(0\)[\-\s]?)?|0)6((\s|\s?\-\s?)?[0-9]){8}$/i
  //     )
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "mobileRU", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     value = value.replace(/\(|\)|\s+|-/g, "");
  //     errorMessage = "Please specify a valid mobile number";
  //     return value.match(/^((\+7|7|8)+([0-9]){10})$/i) && value.length > 9
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });
  // yup.addMethod(yup.string, "mobileUK", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     value = value.replace(/\(|\)|\s+|-/g, "");
  //     errorMessage = "Please specify a valid mobile number";
  //     return value.match(
  //       /^(?:(?:(?:00\s?|\+)44\s?|0)7(?:[1345789]\d{2}|624)\s?\d{3}\s?\d{3})$/i
  //     ) && value.length > 9
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });

  // yup.addMethod(yup.string, "mobile1", function (errorMessage) {
  //   return this.test(
  //     "mobile1",
  //     function () {
  //       //return this.createError();
  //     },
  //     function (value: any) {
  //       return value === yup.ref(errorMessage);
  //     }
  //   );
  // });
  // yup.addMethod(yup.string, "validJson", function (errorMessage) {
  //   return this.test(`test-valid-json`, function (value: any) {
  //     const { path, createError } = this;

  //     try {
  //       JSON.parse(value);
  //     } catch (e) {
  //       return createError({ path, message: errorMessage });
  //     }

  //     return true;
  //   });
  // });

  // yup.addMethod(yup.string, "sameAsTarget", function (errorMessage) {
  //   return this.test(function (value: any) {
  //     const { path, createError } = this;
  //     return value == 123456
  //       ? true
  //       : createError({ path, message: errorMessage });
  //   });
  // });

  const ASYNC_VALIDATION_TIMEOUT_IN_MS = 500;
  const validationFunction = async (value, resolve) => {
    // try {
    //   const response = (await fetch("/is-jimmy/" + value)) as any;
    //   resolve(response.responseText === "true");
    // } catch (error) {
    //   resolve(false);
    // }

    try {
      const axiosRequestConfig: any = Object.assign({
        username: value,
      });
      CHECK_USERNAME_LOADING.value = true;
      ApiService.setHeader();
      ApiService.post("/account/checkUsername", axiosRequestConfig)
        .then(({ data }) => {
          const { error_code, error_desc } = data;
          let is = false;
          if (error_code == 0) {
            is = true;
          }
          resolve(is);
        })
        .catch(({ response }) => {
          resolve(false);
        })
        .finally(() => {
          CHECK_USERNAME_LOADING.value = false;
        });
    } catch (error) {
      resolve(false);
    }
  };
  const validationDebounced = debounce(
    validationFunction,
    ASYNC_VALIDATION_TIMEOUT_IN_MS
  );

  //异步Ajax校验用户名是否被占用
  yup.addMethod(
    yup.string,
    "checkUsername",
    function (errorMessage = "用户名已被占用，请更换") {
      return this.test({
        //yupSequence({
        //"checkUsername",
        //errorMessage,
        //yup.string().required().email("Enter a valid email"),
        //yup.string().test({
        name: "checkUsername",
        message: errorMessage,
        exclusive: true,
        test: (value: any, context: any) => {
          const { path, createError } = context;
          const field = context.from[0].schema.fields.username;

          if (field.oldValue == undefined || value == "") {
            field.oldValue = value;
            return true;
          } else if (field.oldValue == value) {
            return field.isCheckUsername == false ? false : true;
          }

          field.isCheckUsername = false;
          field.oldValue = value;
          const promise = new Promise((resolve) =>
            validationDebounced(value, resolve)
          );
          promise
            .then((res) => {
              field.isCheckUsername = res;
              if (!res) {
                createError({ path, message: errorMessage });
                return true;
              }
            })
            .catch((reason) => {
              return false;
            });
          return promise as any;
        },
      });
    }
  );

  // .test(
  //   "youtube-api",
  //   "video not found or not embeddable",
  //   async (value) => {
  //     console.log("youtube-api", value);
  //     const parts = "snippet,contentDetails,status";
  //     // const response = await fetch(
  //     //   `/videos?id=${value}&key=&part=${parts}`
  //     // );
  //     // const body = await response.json();
  //     return true;
  //   },
  //   ["username"]
  // )
  // .test({
  //   name: "is-jimmy",
  //   message: "${path} is not Jimmy",
  //   test: (value) =>
  //     new Promise((resolve) => validationDebounced(value, resolve)),
  // })
  // .test(
  //   {
  //     name: "is-jimmy",
  //     exclusive: true,
  //     params: { max: 5 },
  //     message: "${path} must be less than ${max} characters",
  //     test: async (value, testContext) =>
  //       ((await fetch("/is-jimmy/" + value)) as any).responseText ===
  //       "true",
  //   }

  //   // "is-jimmy",
  //   // ({ label }) => `${label} is not Jimmy`, // a message can also be a function
  //   // async (value, testContext) =>
  //   //   ((await fetch("/is-jimmy/" + value)) as any).responseText ===
  //   //   "true",
  //   //   true
  // )
  //.username()

  // .test(
  //   "checkUsername",
  //   "用户名已存在",
  //   (value) =>
  //     new Promise((resolve, reject) => {
  //       console.log("Promise");
  //       const axiosRequestConfig: any = Object.assign({
  //         username: value,
  //       });
  //       ApiService.setHeader();
  //       ApiService.post("/account/checkUsername", axiosRequestConfig)
  //         .then(({ data }) => {
  //           const { error_code, error_desc } = data;
  //           let is = false;
  //           if (error_code == 0) {
  //             is = true;
  //           }
  //           resolve(is);
  //         })
  //         .catch(({ response }) => {
  //           resolve(false);
  //           console.log(response);
  //         })
  //         .finally(() => {
  //           //loading.value = false;
  //         });
  //     })
  // )
}
